/** Every catalog, added-activity and space-people read these screens make, keyed for invalidation. */

import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  InfiniteData,
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  ActivityPage,
  ActivityRoleAssignment,
  ActivitySummary,
  AddedActivity,
  BindingTargetKind,
  BundleResolution,
  CapabilityFacet,
  ConfiguredGates,
  DirectoryGrant,
  EntityKeying,
  Gate,
  HeldPermits,
  InvitationCode,
  IssuedInvitation,
  Membership,
  Permit,
  PublishedVersion,
  RequirementBinding,
  VersionAvailability,
  StartBundleResult,
  StopBundleResult,
  StoreRollup,
} from "@nebulize/sdk";

/** `VersionPolicy`, derived rather than imported: the generated barrel does not export it by name. */
export type VersionPolicy = NonNullable<AddedActivity["version_policy"]>;

// Re-exported so the screen imports its shapes from one place, and that place is generated.
export type { CapabilityFacet, CapabilityFacetKind, EntityKeying } from "@nebulize/sdk";

import { api, request } from "@/lib/api";

import { entryOf } from "../../spaces/lib/present";
import { spaceKeys } from "../../spaces/lib/queries";
import type { CatalogEntry } from "../../types";

/** `InterfaceRegistryEntry`, hand-typed until the model reaches `sdk`. */
export interface InterfaceEntry {
  name: string;
  summary: string;
  lifecycle: string;
  kind: string;
  provisional: boolean;
}

export const catalogKeys = {
  rollup: (activityId: string) => ["activities", activityId, "rollup"] as const,
  versions: (activityId: string) => ["activities", activityId, "versions"] as const,
  bundle: (activityId: string) => ["activities", activityId, "bundle"] as const,
  interfaces: ["interfaces"] as const,
  library: (query: CapabilityQueryInput) => ["activities", "library", query] as const,
};


/** The facets `GET /v1/activities` takes (CAT-027). Two values under one kind are ORed. */
export interface CapabilityQueryInput {
  requires?: string[];
  provides?: string[];
  exports?: string[];
  triggers?: string[];
  effects?: string[];
  abilities?: string[];
  entity_types?: string[];
  keyed_by?: EntityKeying[];
  canvas_kinds?: string[];
  reads?: string[];
  writes?: string[];
}

const FACET_KEYS = [
  "requires",
  "provides",
  "exports",
  "triggers",
  "effects",
  "abilities",
  "entity_types",
  "keyed_by",
  "canvas_kinds",
  "reads",
  "writes",
] as const satisfies readonly (keyof CapabilityQueryInput)[];

/** Whether a query names no facet at all. An empty query is an ordinary, unnarrowed browse. */
export function isEmptyCapabilityQuery(query: CapabilityQueryInput): boolean {
  return FACET_KEYS.every((key) => (query[key] ?? []).length === 0);
}

export interface CatalogSearchPage {
  activities: ActivitySummary[];
  unmatchedFacets: CapabilityFacet[];
  nextCursor: string | null;
}

/** CAT-027: the catalog narrowed by capability, paged. Never fetches the whole catalog to filter it. */
export function useCatalogSearch(
  query: CapabilityQueryInput,
  enabled = true,
): UseInfiniteQueryResult<InfiniteData<CatalogSearchPage>, Error> {
  return useInfiniteQuery({
    queryKey: catalogKeys.library(query),
    enabled,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set("limit", "24");
      // A withdrawn or deprecated version is kept and labelled, never silently missing (CAT-017).
      params.set("include_unaddable", "true");
      if (pageParam !== null) params.set("cursor", pageParam);
      for (const key of FACET_KEYS) {
        for (const value of query[key] ?? []) params.append(key, String(value));
      }
      const page = await api.get<ActivityPage>(`/v1/activities?${params.toString()}`);
      return {
        activities: page.activities ?? [],
        unmatchedFacets: page.unmatched_facets ?? [],
        nextCursor: page.next_cursor ?? null,
      };
    },
    getNextPageParam: (last) => last.nextCursor,
  });
}

export const addedKeys = {
  gates: (addedActivityId: string) =>
    ["spaces", "added-activity", addedActivityId, "gates"] as const,
  roles: (addedActivityId: string) =>
    ["spaces", "added-activity", addedActivityId, "roles"] as const,
};

export const peopleKeys = {
  members: (spaceId: string) => ["spaces", spaceId, "members"] as const,
  invitations: (spaceId: string) => ["spaces", spaceId, "invitations"] as const,
  grants: (spaceId: string) => ["spaces", spaceId, "directory-grants"] as const,
  permits: (spaceId: string) => ["spaces", spaceId, "permits"] as const,
};

/** Newest first, comparing each dotted part as a number so 1.10.0 outranks 1.9.0. */
function newestFirst(left: PublishedVersion, right: PublishedVersion): number {
  return right.version.localeCompare(left.version, undefined, { numeric: true });
}

export function useRollup(activityId: string): UseQueryResult<StoreRollup> {
  return useQuery({
    queryKey: catalogKeys.rollup(activityId),
    enabled: activityId !== "",
    staleTime: 5 * 60 * 1000,
    queryFn: () => api.get<StoreRollup>(`/v1/activities/${activityId}/rollup`),
  });
}

export function useVersions(activityId: string): UseQueryResult<PublishedVersion[]> {
  return useQuery({
    queryKey: catalogKeys.versions(activityId),
    enabled: activityId !== "",
    staleTime: 5 * 60 * 1000,
    queryFn: async () =>
      [...(await api.get<PublishedVersion[]>(`/v1/activities/${activityId}/versions`))].sort(
        newestFirst,
      ),
  });
}

export interface CatalogEntryQuery {
  entry: CatalogEntry | undefined;
  query: UseQueryResult<PublishedVersion[]>;
}

/** One catalog entry off the newest published version, carrying every version it has. */
export function useCatalogEntry(activityId: string): CatalogEntryQuery {
  const query = useVersions(activityId);
  const newest = query.data?.[0];
  return {
    entry:
      newest === undefined
        ? undefined
        : { ...entryOf(newest), versions: (query.data ?? []).map((one) => one.version) },
    query,
  };
}

export function useBundleResolution(
  activityId: string,
  enabled: boolean,
): UseQueryResult<BundleResolution> {
  return useQuery({
    queryKey: catalogKeys.bundle(activityId),
    enabled: enabled && activityId !== "",
    staleTime: 5 * 60 * 1000,
    queryFn: () => api.get<BundleResolution>(`/v1/activities/${activityId}/bundle`),
  });
}

export function useInterfaces(): UseQueryResult<InterfaceEntry[]> {
  return useQuery({
    queryKey: catalogKeys.interfaces,
    staleTime: 5 * 60 * 1000,
    queryFn: () => api.get<InterfaceEntry[]>("/v1/interfaces"),
  });
}

export function useBindings(addedActivityId: string): UseQueryResult<RequirementBinding[]> {
  return useQuery({
    queryKey: spaceKeys.bindings(addedActivityId),
    enabled: addedActivityId !== "",
    queryFn: () =>
      api.get<RequirementBinding[]>(`/v1/added-activities/${addedActivityId}/bindings`),
  });
}

export function useGates(addedActivityId: string): UseQueryResult<Gate[]> {
  return useQuery({
    queryKey: addedKeys.gates(addedActivityId),
    enabled: addedActivityId !== "",
    queryFn: async () =>
      (await api.get<ConfiguredGates>(`/v1/added-activities/${addedActivityId}/gates`)).gates ?? [],
  });
}

export interface ActivityGates {
  added_activity_id: string;
  gates: Gate[];
}

/** The gates of many added activities at once, so a screen can hide a panel that holds none. */
export function useGatesFor(addedActivityIds: readonly string[]): ActivityGates[] {
  const results = useQueries({
    queries: addedActivityIds.map((one) => ({
      queryKey: addedKeys.gates(one),
      queryFn: async () =>
        (await api.get<ConfiguredGates>(`/v1/added-activities/${one}/gates`)).gates ?? [],
    })),
  });
  return addedActivityIds.map((one, index) => ({
    added_activity_id: one,
    gates: results[index]?.data ?? [],
  }));
}

export function useActivityRoles(
  addedActivityId: string,
): UseQueryResult<ActivityRoleAssignment[]> {
  return useQuery({
    queryKey: addedKeys.roles(addedActivityId),
    enabled: addedActivityId !== "",
    queryFn: () =>
      api.get<ActivityRoleAssignment[]>(`/v1/added-activities/${addedActivityId}/roles`),
  });
}

export function useMembers(spaceId: string): UseQueryResult<Membership[]> {
  return useQuery({
    queryKey: peopleKeys.members(spaceId),
    enabled: spaceId !== "",
    queryFn: () => api.get<Membership[]>(`/v1/spaces/${spaceId}/members`),
  });
}

export function useInvitations(spaceId: string): UseQueryResult<InvitationCode[]> {
  return useQuery({
    queryKey: peopleKeys.invitations(spaceId),
    enabled: spaceId !== "",
    queryFn: () => api.get<InvitationCode[]>(`/v1/spaces/${spaceId}/invitations`),
  });
}

export function useDirectoryGrants(spaceId: string): UseQueryResult<DirectoryGrant[]> {
  return useQuery({
    queryKey: peopleKeys.grants(spaceId),
    enabled: spaceId !== "",
    queryFn: () => api.get<DirectoryGrant[]>(`/v1/spaces/${spaceId}/directory-grants`),
  });
}

export function usePermits(spaceId: string): UseQueryResult<Permit[]> {
  return useQuery({
    queryKey: peopleKeys.permits(spaceId),
    enabled: spaceId !== "",
    queryFn: async () =>
      (await api.get<HeldPermits>(`/v1/spaces/${spaceId}/permits`)).permits ?? [],
  });
}

function useSpaceInvalidation(): () => void {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: spaceKeys.all });
  };
}

export interface BindInput {
  requirement_name: string;
  position: number;
  target_kind: BindingTargetKind;
  target_id: string;
}

export function useBind(
  addedActivityId: string,
): UseMutationResult<RequirementBinding, Error, BindInput> {
  const invalidate = useSpaceInvalidation();
  return useMutation({
    mutationFn: (body: BindInput) =>
      api.post<RequirementBinding>(`/v1/added-activities/${addedActivityId}/bindings`, body),
    onSuccess: invalidate,
  });
}

export interface RebindInput {
  binding_id: string;
  target_kind: BindingTargetKind;
  target_id: string;
}

/** Refill a position that already holds a binding. One call, so it is never briefly unwired. */
export function useRebind(): UseMutationResult<RequirementBinding, Error, RebindInput> {
  const invalidate = useSpaceInvalidation();
  return useMutation({
    mutationFn: ({ binding_id, ...body }: RebindInput) =>
      api.post<RequirementBinding>(`/v1/bindings/${binding_id}/rebind`, body),
    onSuccess: invalidate,
  });
}

export function useUnbind(): UseMutationResult<undefined, Error, string> {
  const invalidate = useSpaceInvalidation();
  return useMutation({
    mutationFn: (bindingId: string) => api.delete<undefined>(`/v1/bindings/${bindingId}`),
    onSuccess: invalidate,
  });
}

export interface InviteInput {
  added_activity_id: string;
  requirement_name: string;
  position: number;
}

export function useIssueInvitation(
  spaceId: string,
): UseMutationResult<IssuedInvitation, Error, InviteInput> {
  const invalidate = useSpaceInvalidation();
  return useMutation({
    mutationFn: (body: InviteInput) =>
      api.post<IssuedInvitation>(`/v1/spaces/${spaceId}/invitations`, body),
    onSuccess: invalidate,
  });
}

export interface AssignRoleInput {
  role: string;
  account_id?: string;
  catch_all?: boolean;
}

export function useAssignRole(
  addedActivityId: string,
): UseMutationResult<ActivityRoleAssignment, Error, AssignRoleInput> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: AssignRoleInput) =>
      api.post<ActivityRoleAssignment>(`/v1/added-activities/${addedActivityId}/roles`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: addedKeys.roles(addedActivityId) });
    },
  });
}

export function useRevokeRoleAssignment(
  addedActivityId: string,
): UseMutationResult<undefined, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) =>
      api.delete<undefined>(`/v1/activity-role-assignments/${assignmentId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: addedKeys.roles(addedActivityId) });
    },
  });
}

export function useSetGates(
  addedActivityId: string,
): UseMutationResult<ConfiguredGates, Error, Gate[]> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (gates: Gate[]) =>
      request<ConfiguredGates>(`/v1/added-activities/${addedActivityId}/gates`, {
        method: "PUT",
        body: JSON.stringify({ gates }),
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: addedKeys.gates(addedActivityId) });
    },
  });
}

export interface VersionPolicyEdit {
  addedActivityId: string;
  versionPolicy: VersionPolicy;
}

/** Whether a published update reaches this added activity by itself (SES-024). */
export function useSetVersionPolicy(): UseMutationResult<
  AddedActivity,
  Error,
  VersionPolicyEdit
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ addedActivityId, versionPolicy }: VersionPolicyEdit) =>
      api.post<AddedActivity>(`/v1/added-activities/${addedActivityId}/version-policy`, {
        version_policy: versionPolicy,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: spaceKeys.all });
    },
  });
}

export type BundleVerb = "start" | "stop";

export function useBundleVerb(
  addedActivityId: string,
): UseMutationResult<StartBundleResult | StopBundleResult, Error, BundleVerb> {
  const invalidate = useSpaceInvalidation();
  return useMutation({
    mutationFn: (verb: BundleVerb) =>
      api.post<StartBundleResult | StopBundleResult>(
        `/v1/added-activities/${addedActivityId}/${verb}-bundle`,
      ),
    onSuccess: invalidate,
  });
}

export interface Ruling {
  activityId: string;
  version: string;
  availability: VersionAvailability;
  reason: string;
}

/**
 * Retiring a published version, which is what this product has instead of delete. A version is
 * immutable and is never removed: `deprecated` still starts, `withdrawn` stops being addable and
 * `revoked` stops running, and `published` puts any of them back.
 */
export function useRuleAvailability(): UseMutationResult<PublishedVersion, Error, Ruling> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ activityId, version, availability, reason }: Ruling) =>
      api.post<PublishedVersion>(
        `/v1/activities/${activityId}/versions/${version}/availability`,
        { availability, reason },
      ),
    onSuccess: (_answer, ruled) => {
      void client.invalidateQueries({ queryKey: catalogKeys.versions(ruled.activityId) });
      void client.invalidateQueries({ queryKey: ["activities"] });
    },
  });
}
